// Phase II · Wave C.5a · Editorial route guard.
// Wraps ProtectedRoute · additionally checks `/api/v9/blog/editorial-roles/me`
// to confirm the user has an editorial role (editor / contributor /
// guest_author) OR an admin role (admin/super_admin). Redirects to
// `/app/dashboard` with a toast on failure.
import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { isAdminRole } from "@/data/v12_user_nav";
import { blogAdminApi } from "@/lib/api/blog_admin";
import V12 from "./_theme";

export default function EditorialRouteGuard({
    children, adminOnly = false,
}) {
    const { user, loading } = useAuth();
    const location = useLocation();
    const [roleProbe, setRoleProbe] = useState({ status: "loading", data: null });

    useEffect(() => {
        if (loading || !user) return;
        let cancelled = false;
        (async () => {
            try {
                const data = await blogAdminApi.myEditorialRole();
                if (!cancelled) setRoleProbe({ status: "ok", data });
            } catch {
                if (!cancelled) setRoleProbe({ status: "error", data: null });
            }
        })();
        return () => { cancelled = true; };
    }, [loading, user]);

    if (loading || (user && roleProbe.status === "loading")) {
        return (
            <div
                data-testid="editorial-guard-loading"
                className={`min-h-screen flex items-center justify-center bg-neutral-950 ${V12.textSecondary}`}
            >
                <div className="flex items-center gap-3 text-sm">
                    <span className="h-2 w-2 rounded-full bg-neutral-500 animate-pulse" />
                    <span>Checking editorial access…</span>
                </div>
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/" state={{ from: location }} replace />;
    }

    // Admin-only check (SitemapStats, EditorialRolesManager).
    if (adminOnly && !isAdminRole(user.role)) {
        toast.error("Permessi insufficienti · richiesto admin");
        return <Navigate to="/app/dashboard" replace />;
    }

    // Editorial access: admin bypass OR effective role present.
    const probe = roleProbe.data || {};
    const hasEditorial = probe.is_admin
        || Boolean(probe.effective_role)
        || isAdminRole(user?.role);

    if (!hasEditorial) {
        toast.error("Accesso editoriale richiesto · richiedi un ruolo editorial");
        return <Navigate to="/app/dashboard" replace />;
    }

    // Expose the probe to downstream pages via a context prop if useful.
    return React.cloneElement(children, {
        editorialProbe: probe,
    });
}
